import "~/stylesheets/application.css";

import { polyfillCountryFlagEmojis } from "country-flag-emoji-polyfill";
import flagFontUrl from "country-flag-emoji-polyfill/dist/TwemojiCountryFlags.woff2?url";

// Windows has no flag emoji: Chrome and Edge show "IN" instead of the Indian flag, which breaks every
// flag game there. Loads a flags-only font (self-hosted) on those browsers and nowhere else; the
// font is first in the font-family lists in application.css.
polyfillCountryFlagEmojis("Twemoji Country Flags", flagFontUrl);

import { Application } from "@hotwired/stimulus";
import "@hotwired/turbo-rails"; // starts Turbo Drive on import
import { registerControllers } from "stimulus-vite-helpers";

import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { frontendConfig } from "~/utils/frontendConfig";

// A failed import(): a chunk an earlier deploy removed (Back restores a page from Turbo's cache
// without asking the server, so its game asks for the old file), the connection, or the reader
// leaving the page, which cancels what is still loading (Safari: "Importing a module script failed",
// an iPhone that left Pinpoint a second after opening it). Only the first two are worth an error,
// so Sentry waits a moment and drops it if the page has been left or hidden in the meantime. iOS
// fires no beforeunload and pagehide only when the next page arrives, which is why nothing here
// reloads the page by itself: it could take the reader back to the page they were leaving.
const MODULE_LOAD_ERROR = /dynamically imported module|importing a module script failed|unable to preload css/i;
const LEAVE_WAIT_MS = 2000;
let leavingPage = false;
window.addEventListener("pagehide", () => { leavingPage = true; });
document.addEventListener("turbo:visit", () => { leavingPage = true; });
window.addEventListener("pageshow", () => { leavingPage = false; });
document.addEventListener("turbo:load", () => { leavingPage = false; });

// Initialize Sentry for frontend error tracking (production only).
// The DSN comes from Rails credentials via a <meta> tag - see FrontendConfigHelper.
const sentryDsn = frontendConfig("sentry-dsn");
if (import.meta.env.PROD && sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.1, // 10% of transactions for performance monitoring
    // Not ours: link scanners that open pages in an embedded browser (reported with Outlook Safe Links and
    // CefSharp) reject a promise with this plain string. getsentry/sentry-javascript#3440 suggests an exact regex.
    ignoreErrors: [/Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/],
    beforeSend: (event) => {
      if (!event.exception?.values?.some((value) => MODULE_LOAD_ERROR.test(value.value ?? ""))) return event;
      return new Promise((resolve) => {
        window.setTimeout(() => resolve(leavingPage || document.visibilityState === "hidden" ? null : event), LEAVE_WAIT_MS);
      });
    },
  });
}

// Game components are loaded with import() inside their mount function, so a page downloads only
// the game it shows. Static imports put every game (and Leaflet, via Pinpoint) on every page.
import type { Difficulty } from "~/components/MemoryMap";
import type { Difficulty as FlagFrenzyDifficulty } from "~/components/FlagFrenzy";
import type { Region, RegionDetailData, Belt } from "~/components/MemoryMastery";
import OnboardingProvider from "~/components/shared/OnboardingProvider";
import { SentryErrorBoundary } from "~/components/shared/SentryErrorBoundary";

// Start Stimulus
const application = Application.start();

// Auto-register Stimulus controllers
const controllers = import.meta.glob("../controllers/**/*_controller.ts", {
  eager: true,
});
registerControllers(application, controllers);

// Export for debugging
window.Stimulus = application;

// Roots mounted on the page, with the markup the server rendered in their place
const mountedRoots = new Map<HTMLElement, { root: ReactDOM.Root; placeholder: string }>();
// Containers whose component chunk is still downloading
const pendingContainers = new Set<HTMLElement>();

// In place of a game whose code didn't load: a reload brings the code of the current deploy. The
// reader chooses it, so it never fights a page they are leaving. The server's markup waits in a
// <template> and comes back on the next try (Turbo caches the page with the notice in it), and the
// button works through one listener on the document, so it still works in a cached copy.
const showLoadFailed = (container: HTMLElement) => {
  const original = document.createElement("template");
  original.dataset.loadFailed = "";
  original.innerHTML = container.innerHTML;
  const box = document.createElement("div");
  box.className = "flex flex-col items-center justify-center py-16 px-4 text-center";
  const title = document.createElement("p");
  title.className = "font-semibold text-base-content";
  title.textContent = "This didn't load";
  const hint = document.createElement("p");
  hint.className = "mt-1 text-sm text-base-content/70";
  hint.textContent = "The site was just updated, or the connection dropped.";
  const button = document.createElement("button");
  button.type = "button";
  button.className = "btn btn-primary btn-sm mt-4";
  button.dataset.reloadPage = "";
  button.textContent = "Reload";
  box.append(title, hint, button);
  container.replaceChildren(original, box);
};
document.addEventListener("click", (event) => {
  if (event.target instanceof Element && event.target.closest("[data-reload-page]")) location.reload();
});

// Render a lazily loaded component into its container. Skips the render if the page changed while
// the chunk was loading (Turbo navigation); on a failed load the next turbo:load tries again.
const renderWhenLoaded = (container: HTMLElement, load: () => Promise<React.ReactElement>) => {
  const original = container.querySelector<HTMLTemplateElement>(":scope > template[data-load-failed]");
  if (original) container.replaceChildren(original.content.cloneNode(true));
  pendingContainers.add(container);
  load()
    .then((element) => {
      pendingContainers.delete(container);
      if (!container.isConnected) return;
      const root = ReactDOM.createRoot(container);
      mountedRoots.set(container, { root, placeholder: container.innerHTML });
      root.render(
        <React.StrictMode>
          <SentryErrorBoundary>{element}</SentryErrorBoundary>
        </React.StrictMode>
      );
    })
    .catch((error: unknown) => {
      pendingContainers.delete(container);
      delete container.dataset.reactMounted;
      if (container.isConnected && !leavingPage && error instanceof Error && MODULE_LOAD_ERROR.test(error.message)) {
        showLoadFailed(container);
      }
      throw error; // unhandled rejection, reported by Sentry (see MODULE_LOAD_ERROR)
    });
};

// Leaving a page unmounts its components. Turbo only swaps the body, so a mounted game kept
// running on the next page (Continental Drift's key listener blocked Space and the arrows site
// wide), and the cached snapshot came back on Back with data-react-mounted set and no React in it:
// a dead game. The placeholder goes back in, so Back mounts the component fresh. A container still
// waiting for its chunk loses the flag too, or Back would show its spinner forever.
const unmountRoots = () => {
  mountedRoots.forEach(({ root, placeholder }, container) => {
    try {
      root.unmount();
    } catch (error) {
      Sentry.captureException(error); // one failing cleanup doesn't keep the other roots mounted
    }
    container.innerHTML = placeholder;
    delete container.dataset.reactMounted;
  });
  mountedRoots.clear();
  pendingContainers.forEach(container => { delete container.dataset.reactMounted; });
  pendingContainers.clear();
};
document.addEventListener('turbo:before-cache', unmountRoots);
document.addEventListener('turbo:before-render', unmountRoots);

// Mount Memory Map game
const mountMemoryMap = () => {
  const gameContainer = document.getElementById("memory-map-game");
  if (gameContainer && !gameContainer.dataset.reactMounted) {
    gameContainer.dataset.reactMounted = 'true';
    const difficulty = gameContainer.dataset.difficulty as Difficulty | undefined;
    const autoStart = gameContainer.dataset.autoStart === 'true';
    const isLoggedIn = gameContainer.dataset.loggedIn === 'true';
    const bestScoresRaw = gameContainer.dataset.bestScores;
    const bestScores = bestScoresRaw ? JSON.parse(bestScoresRaw) : undefined;
    renderWhenLoaded(gameContainer, async () => {
      const { MemoryMapGame } = await import("~/components/MemoryMap");
      return <MemoryMapGame initialDifficulty={difficulty} autoStart={autoStart} isLoggedIn={isLoggedIn} bestScores={bestScores} />;
    });
  }
};

// Mount Daily Spin Wheel
const mountDailySpin = () => {
  const spinContainer = document.getElementById("daily-spin-wheel");
  if (spinContainer && !spinContainer.dataset.reactMounted) {
    spinContainer.dataset.reactMounted = 'true';
    const canSpin = spinContainer.dataset.canSpin === 'true';
    const todayResult = spinContainer.dataset.todayResult ? JSON.parse(spinContainer.dataset.todayResult) : null;
    const rewards = spinContainer.dataset.rewards ? JSON.parse(spinContainer.dataset.rewards) : [];
    renderWhenLoaded(spinContainer, async () => {
      const { SpinWheel } = await import("~/components/DailySpin");
      return <SpinWheel rewards={rewards} canSpin={canSpin} todayResult={todayResult} />;
    });
  }
};

// Mount Daily Challenge Game (uses same Memory Map component but with fixed config)
const mountDailyChallengeGame = () => {
  const gameContainer = document.getElementById("daily-challenge-game");
  if (gameContainer && !gameContainer.dataset.reactMounted) {
    gameContainer.dataset.reactMounted = 'true';
    const sessionId = gameContainer.dataset.sessionId;
    const config = gameContainer.dataset.config ? JSON.parse(gameContainer.dataset.config) : null;
    const challengeId = gameContainer.dataset.challengeId;
    const initialMistakes = parseInt(gameContainer.dataset.mistakes || '0', 10);
    const initialMaxMistakes = parseInt(gameContainer.dataset.maxMistakes || '5', 10);
    renderWhenLoaded(gameContainer, async () => {
      const { MemoryMapGame } = await import("~/components/MemoryMap");
      return (
        <MemoryMapGame
          sessionId={sessionId}
          isDailyChallenge={true}
          challengeId={challengeId}
          fixedConfig={config}
          isLoggedIn={true}
          initialMistakes={initialMistakes}
          initialMaxMistakes={initialMaxMistakes}
        />
      );
    });
  }
};

// Mount Weekly Challenge Game: the Memory Mastery board of the session /weekly started
const mountWeeklyChallengeGame = () => {
  const gameContainer = document.getElementById("weekly-challenge-game");
  const sessionId = gameContainer?.dataset.sessionId;
  if (gameContainer && sessionId && !gameContainer.dataset.reactMounted) {
    gameContainer.dataset.reactMounted = 'true';
    renderWhenLoaded(gameContainer, async () => {
      const { WeeklyGame } = await import("~/components/MemoryMastery");
      return <WeeklyGame sessionId={sessionId} />;
    });
  }
};

// Mount Pinpoint game
const mountPinpoint = () => {
  const gameContainer = document.getElementById("pinpoint-game");
  if (gameContainer && !gameContainer.dataset.reactMounted) {
    gameContainer.dataset.reactMounted = 'true';
    const isLoggedIn = gameContainer.dataset.loggedIn === 'true';
    const isPremium = gameContainer.dataset.premium === 'true';
    const bestScoresRaw = gameContainer.dataset.bestScores;
    const bestScores = bestScoresRaw ? JSON.parse(bestScoresRaw) : undefined;
    // /games/pinpoint/usa puts the US states card first
    const featured = gameContainer.dataset.featured === 'usa' ? 'usa' : undefined;
    renderWhenLoaded(gameContainer, async () => {
      const { PinpointApp } = await import("~/components/Pinpoint");
      return <PinpointApp isLoggedIn={isLoggedIn} isPremium={isPremium} bestScores={bestScores} featured={featured} />;
    });
  }
};

// Mount Map Drop (/games/map-drop, /games/map-drop/usa)
const mountMapDrop = () => {
  const gameContainer = document.getElementById("map-drop-game");
  if (gameContainer && !gameContainer.dataset.reactMounted) {
    gameContainer.dataset.reactMounted = 'true';
    const isLoggedIn = gameContainer.dataset.loggedIn === 'true';
    const bestScoresRaw = gameContainer.dataset.bestScores;
    const bestScores = bestScoresRaw ? JSON.parse(bestScoresRaw) : undefined;
    const initialMap = gameContainer.dataset.map === 'usa' ? 'usa' : 'europe';
    renderWhenLoaded(gameContainer, async () => {
      const { MapDropGame } = await import("~/components/MapDrop");
      return <MapDropGame isLoggedIn={isLoggedIn} bestScores={bestScores} initialMap={initialMap} />;
    });
  }
};

// Mount Atlas of Empires (/games/atlas-of-empires/play)
const mountAtlasOfEmpires = () => {
  const container = document.getElementById("atlas-of-empires");
  if (container && !container.dataset.reactMounted) {
    container.dataset.reactMounted = 'true';
    renderWhenLoaded(container, async () => {
      const { AtlasApp } = await import("~/components/AtlasOfEmpires");
      return <AtlasApp />;
    });
  }
};

// Mount Daily Pinpoint (/daily)
const mountDailyPinpoint = () => {
  const gameContainer = document.getElementById("daily-pinpoint");
  if (gameContainer && !gameContainer.dataset.reactMounted) {
    gameContainer.dataset.reactMounted = 'true';
    const isLoggedIn = gameContainer.dataset.loggedIn === 'true';
    renderWhenLoaded(gameContainer, async () => {
      const { DailyPinpointApp } = await import("~/components/DailyPinpoint");
      return <DailyPinpointApp isLoggedIn={isLoggedIn} />;
    });
  }
};

// Mount Flag Frenzy game
const mountFlagFrenzy = () => {
  const gameContainer = document.getElementById("flag-frenzy-game");
  if (gameContainer && !gameContainer.dataset.reactMounted) {
    gameContainer.dataset.reactMounted = 'true';
    const difficulty = gameContainer.dataset.difficulty as FlagFrenzyDifficulty | undefined;
    const isLoggedIn = gameContainer.dataset.loggedIn === 'true';
    const bestScoresRaw = gameContainer.dataset.bestScores;
    const bestScores = bestScoresRaw ? JSON.parse(bestScoresRaw) : undefined;
    renderWhenLoaded(gameContainer, async () => {
      const { FlagFrenzyGame } = await import("~/components/FlagFrenzy");
      return <FlagFrenzyGame initialDifficulty={difficulty} isLoggedIn={isLoggedIn} bestScores={bestScores} />;
    });
  }
};

// Mount Geo Sorter game
const mountGeoSorter = () => {
  const gameContainer = document.getElementById("geo-sorter-game");
  if (gameContainer && !gameContainer.dataset.reactMounted) {
    gameContainer.dataset.reactMounted = 'true';
    const isLoggedIn = gameContainer.dataset.loggedIn === 'true';
    renderWhenLoaded(gameContainer, async () => {
      const { GeoSorterGame } = await import("~/components/GeoSorter");
      return <GeoSorterGame isLoggedIn={isLoggedIn} />;
    });
  }
};

// Mount Geo Invaders game
const mountGeoInvaders = () => {
  const gameContainer = document.getElementById("geo-invaders-game");
  if (gameContainer && !gameContainer.dataset.reactMounted) {
    gameContainer.dataset.reactMounted = 'true';
    const isLoggedIn = gameContainer.dataset.loggedIn === 'true';
    const bestScore = gameContainer.dataset.bestScore ? parseInt(gameContainer.dataset.bestScore, 10) : undefined;
    const bestWave = gameContainer.dataset.bestWave ? parseInt(gameContainer.dataset.bestWave, 10) : undefined;
    renderWhenLoaded(gameContainer, async () => {
      const { GeoInvadersGame } = await import("~/components/GeoInvaders");
      return <GeoInvadersGame isLoggedIn={isLoggedIn} bestScore={bestScore} bestWave={bestWave} />;
    });
  }
};

// Mount Geo Snake game
const mountGeoSnake = () => {
  const gameContainer = document.getElementById("geo-snake-game");
  if (gameContainer && !gameContainer.dataset.reactMounted) {
    gameContainer.dataset.reactMounted = 'true';
    const isLoggedIn = gameContainer.dataset.loggedIn === 'true';
    const bestScores = gameContainer.dataset.bestScores ? JSON.parse(gameContainer.dataset.bestScores) : undefined;
    const bestStreak = gameContainer.dataset.bestStreak ? parseInt(gameContainer.dataset.bestStreak, 10) : undefined;
    renderWhenLoaded(gameContainer, async () => {
      const { GeoSnakeGame } = await import("~/components/GeoSnake");
      return <GeoSnakeGame isLoggedIn={isLoggedIn} bestScores={bestScores} bestStreak={bestStreak} />;
    });
  }
};

// Mount Falling Places game
const mountFallingPlaces = () => {
  const gameContainer = document.getElementById("falling-places-game");
  if (gameContainer && !gameContainer.dataset.reactMounted) {
    gameContainer.dataset.reactMounted = 'true';
    const isLoggedIn = gameContainer.dataset.loggedIn === 'true';
    const bestScores = gameContainer.dataset.bestScores ? JSON.parse(gameContainer.dataset.bestScores) : {};
    const bestLevel = gameContainer.dataset.bestLevel ? parseInt(gameContainer.dataset.bestLevel, 10) : null;
    renderWhenLoaded(gameContainer, async () => {
      const { FallingPlacesGame } = await import("~/components/FallingPlaces");
      return <FallingPlacesGame isLoggedIn={isLoggedIn} bestScores={bestScores} bestLevel={bestLevel} />;
    });
  }
};

// Mount Geo Quiz game
const mountGeoQuiz = () => {
  const gameContainer = document.getElementById("geo-quiz-game");
  if (gameContainer && !gameContainer.dataset.reactMounted) {
    gameContainer.dataset.reactMounted = 'true';
    const isLoggedIn = gameContainer.dataset.loggedIn === 'true';
    const bestScores = gameContainer.dataset.bestScores ? JSON.parse(gameContainer.dataset.bestScores) : {};
    renderWhenLoaded(gameContainer, async () => {
      const { GeoQuizGame } = await import("~/components/GeoQuiz");
      return <GeoQuizGame isLoggedIn={isLoggedIn} bestScores={bestScores} />;
    });
  }
};

// Mount Swipe Atlas - starts dealing as soon as it loads, no menu
const mountSwipeAtlas = () => {
  const gameContainer = document.getElementById("swipe-atlas-game");
  if (gameContainer && !gameContainer.dataset.reactMounted) {
    gameContainer.dataset.reactMounted = 'true';
    const isLoggedIn = gameContainer.dataset.loggedIn === 'true';
    renderWhenLoaded(gameContainer, async () => {
      const { SwipeAtlasGame } = await import("~/components/SwipeAtlas");
      return <SwipeAtlasGame isLoggedIn={isLoggedIn} />;
    });
  }
};

// Mount Continental Drift (/games/continental-drift and /daily)
const mountContinentalDrift = () => {
  const container = document.getElementById("continental-drift-game");
  if (container && !container.dataset.reactMounted) {
    container.dataset.reactMounted = 'true';
    const isLoggedIn = container.dataset.loggedIn === 'true';
    const best = parseInt(container.dataset.bestScore ?? '', 10);
    const initialMode = container.dataset.mode === 'daily' ? 'daily' : 'classic';
    // Development tools: frame stats and a load test without the server
    const params = new URLSearchParams(window.location.search);
    const devTools = import.meta.env.DEV;
    renderWhenLoaded(container, async () => {
      const { ContinentalDriftGame } = await import("~/components/ContinentalDrift");
      return (
        <ContinentalDriftGame
          isLoggedIn={isLoggedIn}
          bestScore={Number.isFinite(best) ? best : null}
          initialMode={initialMode}
          debug={devTools && params.get('debug') === '1'}
          stress={devTools ? parseInt(params.get('stress') ?? '0', 10) || 0 : 0}
          bot={devTools && (params.get('bot') === 'random' || params.get('bot') === 'smart') ? params.get('bot') as 'random' | 'smart' : undefined}
        />
      );
    });
  }
};

// Mount Memory Mastery Hub
const mountMasteryHub = () => {
  const container = document.getElementById("memory-mastery-hub");
  if (container && !container.dataset.reactMounted) {
    container.dataset.reactMounted = 'true';
    const regions: Region[] = container.dataset.regions ? JSON.parse(container.dataset.regions) : [];
    const isPremium = container.dataset.premium === 'true';
    const isSignedIn = container.dataset.signedIn === 'true';
    renderWhenLoaded(container, async () => {
      const { MasteryHub } = await import("~/components/MemoryMastery");
      return <MasteryHub regions={regions} isPremium={isPremium} isSignedIn={isSignedIn} />;
    });
  }
};

// Mount Memory Mastery Region Detail
const mountMasteryRegion = () => {
  const container = document.getElementById("memory-mastery-region");
  if (container && !container.dataset.reactMounted) {
    container.dataset.reactMounted = 'true';
    const region = container.dataset.region || '';
    const regionData: RegionDetailData = container.dataset.regionData ? JSON.parse(container.dataset.regionData) : null;
    const isPremium = container.dataset.premium === 'true';
    if (regionData) {
      renderWhenLoaded(container, async () => {
        const { RegionDetail } = await import("~/components/MemoryMastery");
        return <RegionDetail region={region} regionData={regionData} isPremium={isPremium} />;
      });
    }
  }
};

// Mount Memory Mastery Belt Game
const mountMasteryGame = () => {
  const container = document.getElementById("memory-mastery-game");
  if (container && !container.dataset.reactMounted) {
    container.dataset.reactMounted = 'true';
    const region = container.dataset.region || '';
    const belt = parseInt(container.dataset.belt || '1', 10);
    const beltData: Belt = container.dataset.beltData ? JSON.parse(container.dataset.beltData) : null;
    const regionData: RegionDetailData = container.dataset.regionData ? JSON.parse(container.dataset.regionData) : null;
    const isPremium = container.dataset.premium === 'true';
    const isLoggedIn = container.dataset.loggedIn === 'true';
    if (beltData && regionData) {
      renderWhenLoaded(container, async () => {
        const { BeltGame } = await import("~/components/MemoryMastery");
        return (
          <BeltGame
            region={region}
            belt={belt}
            beltData={beltData}
            regionData={regionData}
            isPremium={isPremium}
            isLoggedIn={isLoggedIn}
          />
        );
      });
    }
  }
};

// Mount Onboarding Provider (welcome modal for new users) - small and on every page, so static
const mountOnboarding = () => {
  // The layout renders the container for signed-in users only
  const container = document.getElementById("onboarding-provider");
  if (!container) return;

  if (!container.dataset.reactMounted) {
    container.dataset.reactMounted = 'true';
    const root = ReactDOM.createRoot(container);
    mountedRoots.set(container, { root, placeholder: container.innerHTML });
    root.render(
      <React.StrictMode>
        <SentryErrorBoundary>
          <OnboardingProvider />
        </SentryErrorBoundary>
      </React.StrictMode>
    );
  }
};

// Mount all components
const mountAllComponents = () => {
  mountMemoryMap();
  mountDailySpin();
  mountDailyChallengeGame();
  mountWeeklyChallengeGame();
  mountPinpoint();
  mountDailyPinpoint();
  mountFlagFrenzy();
  mountGeoSorter();
  mountGeoInvaders();
  mountGeoSnake();
  mountFallingPlaces();
  mountMapDrop();
  mountAtlasOfEmpires();
  mountGeoQuiz();
  mountSwipeAtlas();
  mountContinentalDrift();
  mountMasteryHub();
  mountMasteryRegion();
  mountMasteryGame();
  mountOnboarding();
};

// Handle initial page load (may have already fired)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAllComponents);
} else {
  mountAllComponents();
}

// Handle Turbo navigation
document.addEventListener('turbo:load', mountAllComponents);
